import { IN_PROGRESS, Status } from "@/reports/config/status";
import {
  ManualValveReport,
  SafetyValveReport,
} from "@/reports/types/SafetyValveReport";
import { Condition } from "@/types/Api";
import { SorterResult } from "antd/es/table/interface";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

type PreferenceStore = {
  rememberedStatus: Status;
  rememberedFilters: Condition[];
  rememberSorter: SorterResult<SafetyValveReport> | undefined;
  rememberedManualValveStatus: Status;
  rememberedManualValveFilters: Condition[];
  rememberedManualValveSorter: SorterResult<ManualValveReport> | undefined;
  setRememberedStatus: (newStatus: Status) => void;
  setRememberedFilters: (newFilters: Condition[]) => void;
  setRememberedSorter: (
    newSorter: SorterResult<SafetyValveReport> | undefined
  ) => void;
  clearRememberedFilters: (defaultValues?: Condition[]) => void;
  clearRememberedSorter: () => void;
  setRememberedManualValveStatus: (newStatus: Status) => void;
  setRememberedManualValveFilters: (newFilters: Condition[]) => void;
  setRememberedManualValveSorter: (
    newSorter: SorterResult<ManualValveReport> | undefined
  ) => void;
  clearRememberedManualValveFilters: (defaultValues?: Condition[]) => void;
  clearRememberedManualValveSorter: () => void;
  clearAllPreferences: () => void;
};

export const usePreferenceStore = create<PreferenceStore>()(
  persist(
    (set) => ({
      rememberedStatus: IN_PROGRESS,
      rememberedFilters: [],
      rememberSorter: undefined,
      rememberedManualValveStatus: IN_PROGRESS,
      rememberedManualValveFilters: [],
      rememberedManualValveSorter: undefined,
      setRememberedStatus: (newStatus: Status) =>
        set((state) => ({ ...state, rememberedStatus: newStatus })),
      setRememberedFilters: (newFilters: Condition[]) =>
        set((state) => ({ ...state, rememberedFilters: newFilters })),
      setRememberedSorter: (
        newSorter: SorterResult<SafetyValveReport> | undefined
      ) => set((state) => ({ ...state, rememberSorter: newSorter })),
      clearRememberedSorter: () =>
        set((state) => ({ ...state, rememberSorter: undefined })),
      clearRememberedFilters: (defaultValues: Condition[] = []) =>
        set((state) => ({ ...state, rememberedFilters: defaultValues })),
      setRememberedManualValveStatus: (newStatus: Status) =>
        set((state) => ({ ...state, rememberedManualValveStatus: newStatus })),
      setRememberedManualValveFilters: (newFilters: Condition[]) =>
        set((state) => ({
          ...state,
          rememberedManualValveFilters: newFilters,
        })),
      setRememberedManualValveSorter: (
        newSorter: SorterResult<ManualValveReport> | undefined
      ) =>
        set((state) => ({ ...state, rememberedManualValveSorter: newSorter })),
      clearRememberedManualValveFilters: (defaultValues: Condition[] = []) =>
        set((state) => ({
          ...state,
          rememberedManualValveFilters: defaultValues,
        })),
      clearRememberedManualValveSorter: () =>
        set((state) => ({ ...state, rememberedManualValveSorter: undefined })),
      clearAllPreferences: () =>
        set((state) => ({
          ...state,
          rememberedFilters: [],
          rememberSorter: undefined,
          rememberedStatus: IN_PROGRESS,
          rememberedManualValveFilters: [],
          rememberedManualValveSorter: undefined,
          rememberedManualValveStatus: IN_PROGRESS,
        })),
    }),

    {
      name: "preference-storage",
      storage: createJSONStorage(() => localStorage),
      version: 1,
    }
  )
);
